import maplibregl from 'maplibre-gl'
import { defineStore } from 'pinia'
import { markRaw } from 'vue'

import {
  DEFAULT_MAP_STYLE_PREFERENCE,
  normalizeMapStylePreference,
  type MapStylePreference,
} from '@/config/mapStyle'
import { useUiStore } from '@/stores/uiStore'

interface MapLayer {
  id: string
  type: string
  [key: string]: unknown
}

interface MapInstance {
  getStyle(): { layers?: MapLayer[] }
  getLayoutProperty(layerId: string, name: string): string | undefined
  getLayer(id: string): unknown
  setLayoutProperty(layerId: string, name: string, value: string): void
  getCenter?(): { lng: number; lat: number }
}

type LayerCategoryMap = Record<string, string[]>

const pointOfInterestLayers = [
  'pois_beach',
  'pois_forest',
  'pois_marina',
  'pois_park',
  'pois_zoo',
  'pois_garden',
  'pois_aerodrome',
  'pois_airport',
  'pois_airfield',
  'pois_helipad',
  'pois_station',
  'pois_bus_stop',
  'pois_ferry_terminal',
  'pois_stadium',
  'pois_university',
  'pois_library',
  'pois_school',
  'pois_animal',
  'pois_post_office',
  'pois_building',
  'pois_townhall',
  'pois_restaurant',
  'pois_fast_food',
  'pois_cafe',
  'pois_bar',
  'pois_hotel',
  'pois_motel',
  'pois_hostel',
  'pois_guest_house',
  'pois_lodging',
  'pois_supermarket',
  'pois_convenience',
  'pois_books',
  'pois_beauty',
  'pois_electronics',
  'pois_clothes',
  'pois_attraction',
  'pois_museum',
  'pois_theatre',
  'pois_artwork',
  'pois_peak',
  'pois_viewpoint',
  'pois_shelter',
  'pois_wilderness_hut',
  'pois_alpine_hut',
  'pois_camp_site',
  'pois_picnic_site',
  'pois_picnic_table',
  'pois_drinking_water',
  'pois_water_point',
  'pois_watering_place',
  'pois_fountain',
  'pois_spring',
  'pois_toilets',
  'pois_bench',
  'pois_information',
  'pois_trailhead',
  'pois_parking',
  'pois_bicycle_parking',
  'pois_bicycle_repair_station',
]

const layerCategories: LayerCategoryMap = {
  base: ['Background', 'background', 'earth'],

  pointsOfInterest: [
    ...pointOfInterestLayers,
    'Outdoor shop',
    'Sport',
    'Transport',
    'Food',
    'Park labels',
    'Healthcare',
    'Tourism',
    'Culture',
    'Outdoor',
    'Station',
    'Castle',
    'Volcano labels',
    'Volcano labels (US)',
    'Peak labels',
    'Peak labels (US)',
  ],

  terrain: [
    'Hillshade',
    'Contour',
    'Contour index',
    'Contour labels',
    'Glacier',
    'Glacier contour',
    'Glacier contour index',
    'Glacier contour labels',
    'Sand',
    'landuse_beach',
    'Cliff',
    'Cliff line',
  ],

  vegetation: [
    'Forest',
    'Tree',
    'Wood',
    'Park',
    'Park outline',
    'Scrub',
    'Grass',
    'landcover',
    'landuse_park',
    'landuse_urban_green',
    'landuse_zoo',
  ],

  water: [
    'Water',
    'water',
    'River',
    'water_river',
    'water_stream',
    'River tunnel',
    'River labels',
    'water_waterway_label',
    'Lake labels',
    'water_label_lakes',
    'Lakeline labels',
    'Ocean labels',
    'water_label_ocean',
    'Sea labels',
    'Outdoor water',
  ],

  urban: [
    'Residential',
    'Industrial',
    'Commercial',
    'Military',
    'Military limit',
    'Cemetery',
    'Hospital',
    'landuse_hospital',
    'Stadium',
    'landuse_industrial',
    'landuse_school',
    'landuse_pedestrian',
  ],

  buildings: ['Building', 'Castle', 'Station', 'buildings'],

  roads: [
    'Road network',
    'Road labels',
    'roads_labels_minor',
    'roads_labels_major',
    'roads_shields',
    'roads_oneway',
    'Bridge',
    'roads_bridges_link_casing',
    'roads_bridges_minor_casing',
    'roads_bridges_major_casing',
    'roads_bridges_highway_casing',
    'roads_bridges_minor',
    'roads_bridges_link',
    'roads_bridges_major',
    'roads_bridges_highway',
    'Tunnel',
    'Tunnel path',
    'roads_tunnels_minor_casing',
    'roads_tunnels_link_casing',
    'roads_tunnels_major_casing',
    'roads_tunnels_highway_casing',
    'roads_tunnels_minor',
    'roads_tunnels_link',
    'roads_tunnels_major',
    'roads_tunnels_highway',
    'Pier',
    'roads_pier',
    'landuse_pier',
    'Pedestrian',
    'Pedestrian outline',
    'roads_minor_service_casing',
    'roads_minor_casing',
    'roads_link_casing',
    'roads_major_casing_early',
    'roads_major_casing_late',
    'roads_highway_casing_early',
    'roads_highway_casing_late',
    'roads_minor_service',
    'roads_minor',
    'roads_link',
    'roads_major',
    'roads_highway',
    'Steps',
  ],

  railways: [
    'Railway tunnel',
    'Railway tunnel dash',
    'Minor railway',
    'Minor railway hatching',
    'Major railway',
    'Major railway dash',
    'roads_rail',
  ],

  aviation: [
    'Aeroway',
    'Runway',
    'roads_runway',
    'landuse_runway',
    'Taxiway',
    'roads_taxiway',
    'Ferry',
    'Airport labels',
    'landuse_aerodrome',
  ],

  trails: [
    'Track',
    'Path',
    'Path minor',
    'Trails outline',
    'Other trails',
    'roads_other',
    'roads_tunnels_other_casing',
    'roads_tunnels_other',
    'roads_bridges_other_casing',
    'roads_bridges_other',
    'roads_paths_hiking',
    'Yellow trail',
    'Green trail',
    'Blue trail',
    'Brown trail',
    'Black trail',
    'Purple trail',
    'Orange trail',
    'Red trail',
    'Longdistance trail',
    'Longdistance trail outline',
    'Longdistance trail labels',
    'Via ferrata',
    'Via ferrata outline',
    'Via ferrata symbol',
  ],

  cycling: [
    'roads_cycleway',
    'Bicycle outline',
    'Bicycle local',
    'Bicycle longdistance',
    'Bicycle longdistance labels',
  ],

  lifts: [
    'roads_aerialway_cable_car',
    'Minor lift',
    'Minor lift dash',
    'Chair lift',
    'Chair lift dash',
    'Cable car',
    'Cable car dash',
    'Aerialway labels',
  ],

  boundaries: [
    'Country border',
    'boundaries_country',
    'Other border',
    'boundaries',
    'Disputed border',
  ],

  labels: [
    'Place labels',
    'places_subplace',
    'Village labels',
    'Town labels',
    'City labels',
    'places_locality',
    'Country labels',
    'places_country',
    'State labels',
    'places_region',
    'Continent labels',
    'earth_label_islands',
    'address_label',
    'water_waterway_label',
    'water_label_ocean',
    'water_label_lakes',
    'roads_oneway',
    'roads_labels_minor',
    'roads_shields',
    'roads_labels_major',
    'Protected area labels',
    'National park labels',
    'Longdistance trail labels',
    'Bicycle longdistance labels',
  ],
}

export const useMapStore = defineStore('mapStore', {
  state: () => ({
    layerVisibility: {} as Record<string, boolean>,
    mapInstance: null as MapInstance | null,
    existingLayers: new Set<string>(),
    savedLayerVisibility: {} as Record<string, boolean>,
    mapStylePreference: { ...DEFAULT_MAP_STYLE_PREFERENCE } as MapStylePreference,
  }),

  actions: {
    setMapStylePreference(preference: MapStylePreference) {
      this.mapStylePreference = normalizeMapStylePreference(preference)
    },

    setMapInstance(map: maplibregl.Map) {
      this.mapInstance = markRaw(map as unknown as MapInstance)
      this.initializeLayers()
    },

    initializeLayers() {
      const layers = this.mapInstance?.getStyle().layers || []

      this.existingLayers.clear()
      const uiStore = useUiStore()

      layers.forEach((layer) => {
        const currentVisibility = this.mapInstance?.getLayoutProperty(layer.id, 'visibility')
        this.layerVisibility[layer.id] = currentVisibility !== 'none'
        this.existingLayers.add(layer.id)
      })

      // Apply saved per-layer visibility. Synchronously update store state and map layout.
      // setLayoutProperty is safe to call during the 'load' event — the style is ready by then.
      Object.entries(uiStore.layerVisibility).forEach(([layerId, isVisible]) => {
        if (this.layerExists(layerId)) {
          this.layerVisibility[layerId] = isVisible
          this.mapInstance?.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none')
        }
      })

      // Default hidden categories if not overridden by user
      const defaultHiddenCategories = ['cycling', 'trails']
      defaultHiddenCategories.forEach((category) => {
        const layers = this.getCategoryLayers(category)
        layers.forEach((layerId) => {
          // Only hide if the user hasn't explicitly set a preference
          if (uiStore.layerVisibility[layerId] === undefined && this.layerExists(layerId)) {
            this.layerVisibility[layerId] = false
            this.mapInstance?.setLayoutProperty(layerId, 'visibility', 'none')
          }
        })
      })
    },

    toggleLayer(layerId: string, visible: boolean) {
      this.layerVisibility[layerId] = visible

      if (this.layerExists(layerId)) {
        this.mapInstance?.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
      }
    },

    toggleCategory(category: string, visible: boolean) {
      const layers = this.getCategoryLayers(category)

      const existingLayers = layers.filter((layerId) => this.layerExists(layerId))

      existingLayers.forEach((layerId) => {
        this.layerVisibility[layerId] = visible
      })

      existingLayers.forEach((layerId) => {
        this.mapInstance?.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
      })
    },

    disableAllLayers() {
      if (!this.mapInstance) return

      this.savedLayerVisibility = { ...this.layerVisibility }

      this.existingLayers.forEach((layerId) => {
        if (this.layerVisibility[layerId]) {
          this.mapInstance?.setLayoutProperty(layerId, 'visibility', 'none')
        }
      })
    },

    restoreLayerVisibility() {
      if (!this.mapInstance) return

      Object.entries(this.savedLayerVisibility).forEach(([layerId, wasVisible]) => {
        if (this.existingLayers.has(layerId) && wasVisible) {
          this.layerVisibility[layerId] = wasVisible

          this.mapInstance?.setLayoutProperty(layerId, 'visibility', 'visible')
        }
      })

      this.savedLayerVisibility = {}
    },

    getCategoryLayers(category: string): string[] {
      return layerCategories[category] || []
    },

    layerExists(layerId: string): boolean {
      return this.existingLayers.has(layerId)
    },

    getCategories(): string[] {
      return Object.keys(layerCategories)
    },
  },

  getters: {
    isCategoryVisible: (state) => {
      return (category: string): boolean => {
        const layers = layerCategories[category] || []
        const existingLayers = layers.filter((id) => state.existingLayers.has(id))
        if (existingLayers.length === 0) return false
        return existingLayers.every((layerId: string) => state.layerVisibility[layerId] !== false)
      }
    },

    isCategoryPartiallyVisible: (state) => {
      return (category: string): boolean => {
        const layers = layerCategories[category] || []
        const existingLayers = layers.filter((id) => state.existingLayers.has(id))
        if (existingLayers.length === 0) return false
        const visibleCount = existingLayers.filter(
          (id) => state.layerVisibility[id] !== false,
        ).length
        return visibleCount > 0 && visibleCount < existingLayers.length
      }
    },
  },
})
